import _ from "lodash";
import ko from "knockout";
import $ from "jquery";
import moment from "moment";
import errorCallbackFactory from "util/errorCallbackFactory";
import momentUtils from "util/momentUtils";
import jstz from "jstz";

/**
 * Decorates the passed in options with information based on the bindindData also passed
 *   in. The settings control the behavior of the dateRangePicker library component.
 * @param bindingData - the binding data for the binding handler.
 * @param options - additional options to set in order to configure the dateRangePicker.
 * @returns {*|{}} The settings object for the dateRangePicker library.
 */
const processOptions = function (bindingData, options) {
    const bindingOptions = bindingData.options;
    const bindingValue = bindingData.value;
    const ranges = [
        {
            name: ko.i18n.renderString("dateRangePicker.today"),
            value: [moment(), moment()],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.yesterday"),
            value: [moment().subtract(1, "days"), moment().subtract(1, "days")],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.priorWeek"),
            value: [
                moment().subtract(1, "week").startOf("week"),
                moment().subtract(1, "week").endOf("week"),
            ],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.priorMonth"),
            value: [
                moment().subtract(1, "month").startOf("month"),
                moment().subtract(1, "month").endOf("month"),
            ],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.weekToDate"),
            value: [moment().startOf("week"), moment().endOf("week")],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.monthToDate"),
            value: [moment().startOf("month"), moment().endOf("month")],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.yearToDate"),
            value: [moment().startOf("year"), moment().endOf("year")],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.last7"),
            value: [moment().subtract(6, "days"), moment()],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.last30"),
            value: [moment().subtract(29, "days"), moment()],
        },
        {
            name: ko.i18n.renderString("dateRangePicker.last90"),
            value: [moment().subtract(89, "days"), moment()],
        },
    ];

    options = options || {};

    /**
     *  Options for localization
     */
    options.dayViewHeaderFormat = "LL";

    // These are marked as dateRangePicker, but they come from here https://github.com/Eonasdan/bootstrap-datetimepicker/blob/master/src/js/bootstrap-datetimepicker.js
    options.tooltips = {
        today: ko.i18n.renderString("dateRangePicker.tooltips.today"),
        clear: ko.i18n.renderString("dateRangePicker.tooltips.clear"),
        close: ko.i18n.renderString("dateRangePicker.tooltips.close"),
        selectMonth: ko.i18n.renderString(
            "dateRangePicker.tooltips.selectMonth"
        ),
        prevMonth: ko.i18n.renderString("dateRangePicker.tooltips.prevMonth"),
        nextMonth: ko.i18n.renderString("dateRangePicker.tooltips.nextMonth"),
        selectYear: ko.i18n.renderString("dateRangePicker.tooltips.selectYear"),
        prevYear: ko.i18n.renderString("dateRangePicker.tooltips.prevYear"),
        nextYear: ko.i18n.renderString("dateRangePicker.tooltips.nextYear"),
        selectDecade: ko.i18n.renderString(
            "dateRangePicker.tooltips.selectDecade"
        ),
        prevDecade: ko.i18n.renderString("dateRangePicker.tooltips.prevDecade"),
        nextDecade: ko.i18n.renderString("dateRangePicker.tooltips.nextDecade"),
        prevCentury: ko.i18n.renderString(
            "dateRangePicker.tooltips.prevCentury"
        ),
        nextCentury: ko.i18n.renderString(
            "dateRangePicker.tooltips.nextCentury"
        ),
        pickHour: ko.i18n.renderString("dateRangePicker.tooltips.pickHour"),
        incrementHour: ko.i18n.renderString(
            "dateRangePicker.tooltips.incrementHour"
        ),
        decrementHour: ko.i18n.renderString(
            "dateRangePicker.tooltips.decrementHour"
        ),
        pickMinute: ko.i18n.renderString("dateRangePicker.tooltips.pickMinute"),
        incrementMinute: ko.i18n.renderString(
            "dateRangePicker.tooltips.incrementMinute"
        ),
        decrementMinute: ko.i18n.renderString(
            "dateRangePicker.tooltips.decrementMinute"
        ),
        pickSecond: ko.i18n.renderString("dateRangePicker.tooltips.pickSecond"),
        incrementSecond: ko.i18n.renderString(
            "dateRangePicker.tooltips.incrementSecond"
        ),
        decrementSecond: ko.i18n.renderString(
            "dateRangePicker.tooltips.decrementSecond"
        ),
        togglePeriod: ko.i18n.renderString(
            "dateRangePicker.tooltips.togglePeriod"
        ),
        selectTime: ko.i18n.renderString("dateRangePicker.tooltips.selectTime"),
    };

    /**
     * Toolbar options
     */
    // will show clear icon in toolbar to clear the calendar
    options.showClear = true;

    if (bindingOptions) {
        // Allow for the clear icon to be disabled in the toolbar. If false, the clear icon
        // will not appear
        if (_.has(bindingOptions, "showClear")) {
            options.showClear = bindingOptions.showClear;
        }

        if (_.has(bindingOptions, "sideBySide")) {
            options.sideBySide = bindingOptions.sideBySide;
        }

        if (bindingOptions.hasCommandButtons) {
            options.hasCommandButtons = bindingOptions.hasCommandButtons;
        }

        if (bindingOptions.intervalSelection && bindingOptions.customRanges) {
            errorCallbackFactory.createCallback({
                namespace: "dateRangePicker.range",
            });
        } else if (bindingOptions.intervalSelection) {
            // Enable defined ranges. If false the dropdown will show both calendars at init.
            options.ranges = ko.observableArray(ranges);
        } else if (bindingOptions.customRanges) {
            options.ranges = ko.observableArray(bindingOptions.customRanges);
        }

        //Get localtime zone
        if (bindingOptions.timeZone) {
            //This will return the time zone name via jstz
            options.timeZone = jstz.determine().name();
        }

        if (bindingOptions.parseInputDate) {
            options.parseInputDate = bindingOptions.parseInputDate;
        }

        // bindingOptions.inline is set to true for dateRangePicker2; this could be optional for
        // single picker
        if (bindingOptions.inline) {
            options.inline = bindingOptions.inline;
        }

        if (bindingOptions.viewMode) {
            options.viewMode = bindingOptions.viewMode;
        }

        if (bindingOptions.viewDate) {
            options.viewDate = bindingOptions.viewDate;
        }

        // Do not let the user pick a date in the future or an end day before the start date.
        if (bindingOptions.isTodayMaxDate) {
            options.maxDate = moment();
        }

        if (bindingOptions.isYesterdayMaxDate) {
            options.maxDate = moment().subtract(1, "days");
        }

        if (
            bindingOptions.minDateDuration &&
            (bindingOptions.isTodayMaxDate || bindingOptions.isYesterdayMaxDate)
        ) {
            options.minDate = moment(options.maxDate).subtract(
                bindingOptions.minDateDuration,
                "days"
            );
        }

        // Set the minimum date that the user may pick
        if (bindingOptions.minDate) {
            options.minDate = bindingOptions.minDate;
        }

        // Set the maximum date that the user may pick
        if (bindingOptions.maxDate) {
            options.maxDate = bindingOptions.maxDate;
        }

        // Sets the toolbar placement on the pickers
        if (bindingOptions.toolbarPlacement) {
            options.toolbarPlacement = bindingOptions.toolbarPlacement;
        }

        /**
         * Sets the format the datePicker will use to read and write it's values.
         */
        if (bindingOptions.format) {
            options.format = bindingOptions.format;
        } else {
            options.format = "YYYY-MM-DDTHH:mm:ss.SSSZZ";
        }

        /**
         * Specifies the time picker control
         */
        if (bindingOptions.timePicker) {
            options.timePicker = bindingOptions.timePicker;
            options.format = "LT";
        }

        /**
         * Specifies the increment that the minutes dropdown will display
         */
        if (bindingOptions.timePickerIncrement) {
            options.timePickerIncrement = bindingOptions.timePickerIncrement;
            options.stepping = bindingOptions.timePickerIncrement;
        } else if (bindingOptions.timePickerStep) {
            options.stepping = bindingOptions.timePickerStep;
        }

        /**
         * Specifies whether to show a seconds dropdown along with hours and minutes
         */
        if (bindingOptions.timePickerSeconds) {
            options.timePickerSeconds = bindingOptions.timePickerSeconds;
            options.format = "LTS";
        }

        /**
         * Specifies whether to use 12 or 24 hour clocks and toggles the AM/PM dropdown
         */
        if (bindingOptions.timePicker12Hour) {
            options.timePicker12Hour = bindingOptions.timePicker12Hour;
            options.format = "HH:mm A";
        }
        /**
         * Specifies widget's positioning
         */
        if (bindingOptions.widgetPositioning) {
            options.widgetPositioning = bindingOptions.widgetPositioning;
        }
        /**
         * Specifies widget's parent
         */
        if (_.has(bindingOptions, "widgetParent")) {
            options.widgetParent = bindingOptions.widgetParent;
        }
        /**
         * Specifies if input should be focused when widget is opened
         */
        if (_.has(bindingOptions, "focusOnShow")) {
            options.focusOnShow = bindingOptions.focusOnShow;
        }
        /**
         * Allows widget to open on input focus
         */
        if (_.has(bindingOptions, "allowInputToggle")) {
            options.allowInputToggle = bindingOptions.allowInputToggle;
        }

        /**
         * Allows widget to use current date upon initial open
         */
        if (_.has(bindingOptions, "useCurrent")) {
            options.useCurrent = bindingOptions.useCurrent;
        }

        /**
         * Changes the heading of the datepicker when in "days" view
         */
        if (_.has(bindingOptions, "dayViewHeaderFormat")) {
            options.dayViewHeaderFormat = bindingOptions.dayViewHeaderFormat;
        }
        /**
         *  OLD PICKER OPTIONS
         */
        // set month and year dropdowns.
        if (bindingOptions.showDropdowns) {
            options.showDropdowns = true;
        }

        if (bindingOptions.opens) {
            options.opens = bindingOptions.opens;
        }

        // set the daterangepicker to be bound to a parent element rather than 'body'.
        //   'parentEl' is a boolean to check.
        //   'parentContainer' is the element to be bound to.
        if (bindingOptions.parentEl) {
            options.parentEl = bindingOptions.parentContainer;
        }

        /**
         * Manually specify the startDate by setting this property in the options.
         *   The format must match the format specified below.
         */
        if (!bindingOptions.singleDatePicker && bindingOptions.startDate) {
            options.startDate = bindingOptions.startDate;
        }

        /**
         * Manually specify the endDate by setting this property in the options.
         *   The format must match the format specified below.
         */
        if (!bindingOptions.singleDatePicker && bindingOptions.endDate) {
            options.endDate = bindingOptions.endDate;
        }

        /**
         * If true the endDate uses the end of day for its value - and not the beginning
         * of the next day.  The difference is 1 millisecond in the range - but the resultant
         * date is off by one when showing the displayed date.
         */
        if (!bindingOptions.singleDatePicker && bindingOptions.useEndOfDay) {
            options.useEndOfDay = bindingOptions.useEndOfDay;
        }

        /**
         * Specifies single date picker.
         */
        if (options.singleDatePicker) {
            if (bindingOptions.startDate) {
                options.startDate = bindingOptions.startDate;
                options.endDate = bindingOptions.startDate;
            }
        } else {
            /**
             * Manually specify the startDate by setting this property in the options.
             *   The format must match the format specified below.
             */
            if (bindingOptions.startDate) {
                options.startDate = bindingOptions.startDate;
            }

            /**
             * Manually specify the endDate by setting this property in the options.
             *   The format must match the format specified below.
             */
            if (bindingOptions.endDate) {
                options.endDate = bindingOptions.endDate;
            }
        }
    }

    //if the bindingOptions.inline option is present it means there are two calendars
    // present using the dateRangePicker2 BH. We need to bypass the setting of start date
    // and end date in this case as these options are not present in the bootstrap date picker lib
    if (bindingValue && !_.has(bindingOptions, "inline")) {
        let dates;
        if (_.isArray(bindingValue)) {
            dates = bindingValue[0].split("/");
        }

        if (_.isString(bindingValue)) {
            dates = bindingValue.split("/");
        }

        if (dates && dates.length === 2) {
            let yesterday;
            if (bindingOptions && bindingOptions.useAsISO) {
                options.startDate = moment(dates[0]).toISOString();
                options.endDate = moment(dates[1]).toISOString();

                if (
                    bindingOptions &&
                    bindingOptions.isTodayMaxDate &&
                    moment(dates[1]).isAfter(moment().endOf("day"))
                ) {
                    options.endDate = moment().endOf("day").toISOString();
                }
                yesterday = moment().subtract(1, "days").endOf("day");
                if (
                    bindingOptions &&
                    bindingOptions.isYesterdayMaxDate &&
                    moment(dates[1]).isAfter(yesterday)
                ) {
                    options.endDate = yesterday.toISOString();
                }
            } else {
                options.startDate = ko.i18n.formatDate(
                    moment(dates[0]).toDate()
                );
                options.endDate = ko.i18n.formatDate(moment(dates[1]).toDate());

                if (
                    bindingOptions &&
                    bindingOptions.isTodayMaxDate &&
                    moment(dates[1]).isAfter(moment().endOf("day"))
                ) {
                    options.endDate = ko.i18n.formatDate(
                        moment().endOf("day").toDate()
                    );
                }
                yesterday = moment().subtract(1, "days").endOf("day");
                if (
                    bindingOptions &&
                    bindingOptions.isYesterdayMaxDate &&
                    moment(dates[1]).isAfter(yesterday)
                ) {
                    options.endDate = ko.i18n.formatDate(yesterday.toDate());
                }
            }

            // Internally Moment uses MM/DD/YYYY for its format.  But elsewhere in the app
            //   we are using ISO string format like 'YYYY-MM-DDTHH:mm:ss.SSSZZ'.  In this
            //   path, though, the dates passed in are converted to their localized
            //   counterpart, but the format is still ISO format.  This makes parsing of
            //   the input dates work incorrectly giving wildly wrong dates.

            // It would be nice to pass in the current locale's date format to moment,
            //   but it is incompatible so we need to match the current locale pattern
            //   in order to allow the dates to be shown correctly.

            // A ticket to generalize and refactor the daterangepicker for consistency
            //   with all users is available here:
            //     https://inindca.atlassian.net/browse/UI-1155
            //   Once completed these formatting adjustments will be unnecessary.

            options.format = ko.i18n.getDateFormatString();
        }
    }

    //For backwards compatibility. In dateRangePicker2 / datePicker2, the locale is sent to the
    // library, in the previous versions the locale strings are established below.
    if (bindingOptions && bindingOptions.hasOwnProperty("useClientLocale")) {
        options.locale = momentUtils.normalizeMomentLocale(ko.i18n.getLocale());
    } else {
        // Custom classes for apply / ok button and cancel button to match spec.
        options.applyClass = "searchFilter-apply";
        options.cancelClass = "searchFilter-cancel";

        // Assign strings to localized strings.
        options.locale = {
            applyLabel: ko.i18n.renderString("dateRangePicker.applyLabel"),
            cancelLabel: ko.i18n.renderString("dateRangePicker.cancelLabel"),
            fromLabel: ko.i18n.renderString("dateRangePicker.fromLabel"),
            toLabel: ko.i18n.renderString("dateRangePicker.toLabel"),
            weekLabel: ko.i18n.renderString("dateRangePicker.weekLabel"),
            customRangeLabel: ko.i18n.renderString(
                "dateRangePicker.customRangeLabel"
            ),
            daysOfWeek: ko.i18n.getShortDaysOfWeek(),
            monthNames: ko.i18n.getShortMonths(),
            firstDay: ko.i18n.getFirstDay(),
        };
    }

    if (bindingOptions && bindingOptions.hasOwnProperty("firstDayOfWeek")) {
        if (bindingOptions.hasOwnProperty("useClientLocale")) {
            // For the new datePickers, we want the current global moment locale
            const clientLocale = momentUtils.normalizeMomentLocale(
                moment.locale()
            );

            if (bindingOptions.firstDayOfWeek) {
                const customName = clientLocale + "_custom";

                // This creates a custom locale for the datePickers to use that inherits from the current locale
                moment.defineLocale(customName, {
                    parentLocale: clientLocale,
                    week: {
                        dow: bindingOptions.firstDayOfWeek,
                    },
                });
                options.locale = customName;

                // defineLocale changes the global locale so change it back to what it was
                moment.locale(momentUtils.normalizeMomentLocale(clientLocale));
            }
        } else {
            options.locale.firstDay = bindingOptions.firstDayOfWeek;
        }
    }

    return options;
};

/**
 * Handles disposal of the bound viewModel.
 * @param disposeElement
 * @param disposalCallback - function to call to remove any additional dependencies.
 */
const handleDisposal = function (disposeElement, disposalCallback) {
    ko.utils.domNodeDisposal.addDisposeCallback(disposeElement, function () {
        const dateRangePicker = $(disposeElement).data("daterangepicker");

        if (dateRangePicker) {
            dateRangePicker.remove();
        }
        if (disposalCallback) {
            disposalCallback();
        }
    });
};

const modifyStyles = function () {
    const cancelButton = $(".daterangepicker")
        .children(".ranges")
        .find(".cancelBtn");
    cancelButton.removeClass("btn btn-small");
};

const customizeCalendarHeader = function (dateRangePicker) {
    if (dateRangePicker) {
        let defaultYearFirst = false;
        const currentLocaleData = moment.localeData();
        if (currentLocaleData && currentLocaleData.longDateFormat) {
            const momentFormat = currentLocaleData.longDateFormat("LL");
            if (momentFormat.indexOf("Y") < momentFormat.indexOf("M")) {
                defaultYearFirst = true;
            }
        }

        dateRangePicker.renderDateString = function (selected) {
            const month = this.locale.monthNames[selected.month()];
            const year = ko.i18n.renderString("dateRangePicker.yearFormat", {
                year: selected.year(),
            });
            return defaultYearFirst ? year + " " + month : month + " " + year;
        };

        dateRangePicker.renderDropdowns = function (
            selected,
            minDate,
            maxDate
        ) {
            const selectedDate = selected || moment();
            const currentMonth = selectedDate.month();
            const currentYear = selectedDate.year();
            const maxYear = (maxDate && maxDate.year()) || currentYear + 5;
            const minYear = (minDate && minDate.year()) || currentYear - 50;

            let monthHtml = '<select class="monthselect">';
            const inMinYear = currentYear == minYear;
            const inMaxYear = currentYear == maxYear;

            for (let m = 0; m < 12; m++) {
                if (
                    (!inMinYear || m >= minDate.month()) &&
                    (!inMaxYear || m <= maxDate.month())
                ) {
                    monthHtml +=
                        "<option value='" +
                        m +
                        "'" +
                        (m === currentMonth ? " selected='selected'" : "") +
                        ">" +
                        this.locale.monthNames[m] +
                        "</option>";
                }
            }
            monthHtml += "</select>";

            let yearHtml = '<select class="yearselect">';

            for (let y = minYear; y <= maxYear; y++) {
                yearHtml +=
                    '<option value="' +
                    y +
                    '"' +
                    (y === currentYear ? ' selected="selected"' : "") +
                    ">" +
                    ko.i18n.renderString("dateRangePicker.yearFormat", {
                        year: y,
                    }) +
                    "</option>";
            }

            yearHtml += "</select>";

            return defaultYearFirst
                ? yearHtml + monthHtml
                : monthHtml + yearHtml;
        };
    }
};

/**
 * This is intended for data formatting only.  Display formatting should be done
 *   through the ko.i18n facilities.
 *
 * This method formats the date information generated on interaction with the calendar.
 *   The default format is ISOString.
 *
 * @param momentObject
 * @param formatKey
 * @returns {*}
 */
const formatForValue = function (momentObject, formatKey) {
    // Note - this is not formatted for display, this is the format used for parsing
    //   and receiving the date information generated in the calendar - for later
    //   interpretation the default format is ISOString.
    if (momentObject === "Invalid date") {
        return ko.i18n.renderString("dateRangePicker.invalidDate");
    } else {
        return formatKey
            ? momentObject.format(formatKey)
            : momentObject.toISOString();
    }
};

export default {
    /**
     * Processes binding data options intended for use with the dateRangePicker library.
     */
    processOptions: processOptions,

    /**
     * Handles disposal of the binding data associated with the bound element.
     */
    handleDisposal: handleDisposal,

    /**
     * Decorates children of '.daterangepicker' library cancel button so the
     *   button classes are removed.  Currently the only style modification
     *   done is removal of the cancel button styles.
     */
    modifyStyles: modifyStyles,

    /**
     * Sets the correct calendar header format based on the locale. Handles whether
     *   the year dropdown is before or after the month dropdown and formats the year
     *   string correctly.
     */
    customizeCalendarHeader: customizeCalendarHeader,

    /**
     * Formats the output generated from clicking the a calendar button to a
     *   format that can be consumed by the model accessing the binding handler.
     *   This is not a display format - it is intended for conversion to a
     *   moment or date for localized display.
     */
    formatForValue: formatForValue,
};
